"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShipTable = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const routes_1 = require("../../../../util/routes");
const compliance_pool_types_1 = require("../../compliance-pool-types");
const common_styled_components_1 = require("../../../common-styled-components");
const compliance_1 = require("../../../../util/compliance/compliance");
const theme_1 = require("../../../../theme");
const amountUtils_1 = require("../../../../util/amountUtils");
const react_1 = require("react");
const Modal_1 = require("../../common/Modal");
const useEdit_1 = __importDefault(require("../../../../hooks/useEdit"));
const useSortedData_1 = require("../../../../hooks/useSortedData");
const lodash_1 = require("lodash");
const ExportCsvButton_1 = require("../ExportCsvButton");
const shipTableColumns = (showPool, showCompany, showDetails, isInAdminTools) => [
    {
        key: "name",
        label: "Name",
        sortableKey: "name",
    },
    {
        key: "company_name",
        label: "Company",
        sortableKey: "company_name",
    },
    {
        key: "verifier",
        label: "Verifier",
        sortableKey: "verifier",
    },
    {
        key: "pool_name",
        label: "Pool",
        sortableKey: "pool_name",
    },
    {
        key: "intensity",
        label: "Intensity (gCO₂e/MJ)",
        sortableKey: "intensity.adjusted_intensity_gCO2e_per_MJ",
    },
    {
        key: "balance",
        label: "+/- (tCO₂e)",
        sortableKey: "intensity.adjusted_emission_balance_ton",
    },
    {
        key: "wind_propulsion",
        label: "Wind propulsion",
        sortableKey: "wind_propulsion",
    },
    {
        key: "ice_class",
        label: "Ice class",
        sortableKey: "ice_class",
    },
    {
        key: "tools",
        label: "",
    },
].filter((c) => (c.key !== "pool_name" || showPool === true) &&
    (c.key !== "company_name" || showCompany === true) &&
    (c.key !== "tools" || isInAdminTools === true) &&
    (c.key !== "transactions.to" || isInAdminTools === true) &&
    (c.key !== "transactions.from" || isInAdminTools === true) &&
    (c.key !== "wind_propulsion" || showDetails === true) &&
    (c.key !== "ice_class" || showDetails === true));
const ShipCellContent = ({ ship, contentKey, compliance, isInAdminTools, removeShip, editShip, userCompanyId, metadata, }) => {
    switch (contentKey) {
        case "name":
            if (isInAdminTools) {
                return ((0, jsx_runtime_1.jsx)(common_styled_components_1.StrippedLink, { to: `${routes_1.routeNames.admin}/${routes_1.routeNames.shipAdmin}/${ship.id}`, style: { color: theme_1.theme.colors.blue }, children: (0, jsx_runtime_1.jsx)(common_styled_components_1.Bold, { children: ship.name }) }));
            }
            else if (userCompanyId !== undefined &&
                ship.company_id === userCompanyId) {
                return ((0, jsx_runtime_1.jsx)(common_styled_components_1.StrippedLink, { to: `${routes_1.routeNames.complianceRoot}/${routes_1.routeNames.ship}/${ship.id}`, style: { color: theme_1.theme.colors.blue }, children: (0, jsx_runtime_1.jsx)(common_styled_components_1.Bold, { children: ship.name }) }));
            }
            return (0, jsx_runtime_1.jsx)("div", { children: ship.name });
        case "pool":
            return (0, jsx_runtime_1.jsx)("div", { children: ship.pool ? ship.pool[2024]?.pool_name || "" : "" });
        case "engines":
            return (0, jsx_runtime_1.jsx)("div", { children: ship.engines.map((e) => e.type).join(", ") });
        case "intensity":
            if (ship.intensity) {
                return ((0, jsx_runtime_1.jsxs)("div", { style: { color: compliance_1.complianceOptions[compliance].color }, children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.ComplianceIcon, { src: compliance_1.complianceOptions[compliance].icon }), (0, amountUtils_1.formatNumber)(ship.intensity.adjusted_intensity_gCO2e_per_MJ, 2)] }));
            }
            else {
                return (0, jsx_runtime_1.jsx)("div", {});
            }
        case "wind_propulsion":
            if (ship.wind_propulsion === null) {
                return (0, jsx_runtime_1.jsx)("div", {});
            }
            return ((0, jsx_runtime_1.jsx)("div", { children: metadata.wind_assisted_propulsion[ship.wind_propulsion.toString()] }));
        case "ice_class":
            if (ship.ice_class === null) {
                return (0, jsx_runtime_1.jsx)("div", {});
            }
            return (0, jsx_runtime_1.jsx)("div", { children: metadata.ice_class[ship.ice_class.toString()] });
        case "balance":
            if (ship.intensity) {
                return ((0, jsx_runtime_1.jsxs)("div", { style: { color: compliance_1.complianceOptions[compliance].color }, children: [ship.intensity.adjusted_emission_balance_ton > 0 ? "+" : "", (0, amountUtils_1.formatNumber)(ship.intensity.adjusted_emission_balance_ton, 2)] }));
            }
            else {
                return (0, jsx_runtime_1.jsx)("div", {});
            }
        case "tools":
            return removeShip && editShip ? ((0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => removeShip(ship), children: "Remove" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => editShip(ship), children: "Edit" })] })) : ((0, jsx_runtime_1.jsx)("div", {}));
        default:
            return (0, jsx_runtime_1.jsx)("div", { children: ship[contentKey] });
    }
};
const ShipTable = ({ ships, metadata, isInAdminTools, userCompanyId, showPool, showCompany, showDetails, refresh, year, }) => {
    const [modal, setModal] = (0, react_1.useState)({});
    const [editStatus, executeEdit] = (0, useEdit_1.default)("admin/ship.json", refresh);
    const { sortedData, sortMethod, sortDirection, setSort } = (0, useSortedData_1.useSortedData)(ships, "id", 1);
    const addShip = () => {
        setModal({
            type: "add",
            value: {
                engines: [
                    {
                        type: 3,
                        ship_engine_type: 1,
                    },
                ],
            },
        });
    };
    const editShip = (ship) => {
        setModal({
            type: "edit",
            value: ship,
        });
    };
    const removeShip = (ship) => {
        setModal({
            type: "remove",
            value: ship,
        });
    };
    const submit = (type, value) => {
        setModal({});
        const shipInput = (0, lodash_1.pick)(value, compliance_pool_types_1.shipInputKeys);
        if (type === "add") {
            if ((0, compliance_pool_types_1.isValidShipInput)(shipInput)) {
                const data = {
                    ...shipInput,
                    id: null,
                };
                executeEdit(data, "PUT");
            }
            else {
                alert("Ship not valid");
            }
        }
        if (type === "edit") {
            if ((0, compliance_pool_types_1.isValidShipInput)(shipInput)) {
                executeEdit(shipInput, "PUT");
            }
            else {
                alert("Ship not valid");
            }
        }
        if (type === "remove") {
            if (shipInput.id !== undefined) {
                const data = {
                    id: shipInput.id,
                };
                executeEdit(data, "DELETE");
            }
        }
    };
    const columns = shipTableColumns(showPool, showCompany, showDetails, isInAdminTools);
    const csvColumns = columns
        .filter((column) => column.key !== "tools")
        .map((column) => ({
        header: column.label,
        getValue: (ship) => {
            switch (column.key) {
                case "name":
                    return ship.name;
                case "company_name":
                    return ship.company_name ?? "";
                case "verifier":
                    return ship.verifier ?? "";
                case "intensity":
                    return ship.intensity
                        ? (0, amountUtils_1.formatNumber)(ship.intensity.adjusted_intensity_gCO2e_per_MJ, 2) ?? ""
                        : "";
                case "balance":
                    if (!ship.intensity) {
                        return "";
                    }
                    return `${ship.intensity.adjusted_emission_balance_ton > 0 ? "+" : ""}${(0, amountUtils_1.formatNumber)(ship.intensity.adjusted_emission_balance_ton, 2) ??
                        ""}`;
                case "wind_propulsion":
                    return ship.wind_propulsion === null
                        ? ""
                        : metadata.wind_assisted_propulsion[ship.wind_propulsion.toString()] ?? "";
                case "ice_class":
                    return ship.ice_class === null
                        ? ""
                        : metadata.ice_class[ship.ice_class.toString()] ?? "";
                default: {
                    const value = ship[String(column.key)];
                    return typeof value === "string" || typeof value === "number"
                        ? value
                        : "";
                }
            }
        },
    }));
    return (metadata && ((0, jsx_runtime_1.jsxs)("div", { children: [modal.type && ((0, jsx_runtime_1.jsx)(Modal_1.Modal, { type: "ship", isOpen: modal.type !== undefined, closeModal: () => setModal({}), modalType: modal.type, value: modal.value, submit: submit })), (0, jsx_runtime_1.jsx)(common_styled_components_1.TitleWithExtraContent, { titleLevel: "Section", extraContent: (0, jsx_runtime_1.jsxs)(common_styled_components_1.Row, { gap: 1, children: [isInAdminTools && (0, jsx_runtime_1.jsx)(common_styled_components_1.Button, { onClick: addShip, children: "+ Add" }), (0, jsx_runtime_1.jsx)(ExportCsvButton_1.ExportCsvButton, { rows: sortedData, columns: csvColumns, tableName: "ships" })] }), children: "Ships" }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.Table, { children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsx)("tr", { children: columns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.SortableTH, { setSort: setSort, sortKey: c.sortableKey, sortDirection: sortMethod && sortMethod === c.sortableKey
                                    ? sortDirection
                                    : undefined, children: c.label }, c.key))) }) }), (0, jsx_runtime_1.jsx)("tbody", { children: sortedData.map((ship) => {
                            const compliance = ship.intensity &&
                                (0, compliance_1.getCompliance)(ship.intensity.adjusted_emission_balance_ton);
                            return ((0, jsx_runtime_1.jsx)(common_styled_components_1.TR, { children: columns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.TD, { children: (0, jsx_runtime_1.jsx)(ShipCellContent, { contentKey: c.key, ship: ship, compliance: compliance, metadata: metadata, isInAdminTools: isInAdminTools, editShip: isInAdminTools ? editShip : undefined, removeShip: isInAdminTools ? removeShip : undefined, userCompanyId: userCompanyId, year: year }) }, c.key))) }, ship.id));
                        }) })] })] })));
};
exports.ShipTable = ShipTable;
